/**
 * Workflow Improvement #001 — Candidate pool panel (candidates first, then
 * assessments). Rendered on the admin recruitment detail page from Stage 3.
 *
 * STEP 1: Add New Candidate (name/email/phone/source/CV) or Search Existing
 *         Candidate (relocated internal-users picker, secondary option).
 * STEP 2: Assign Assessments — enabled only when the pool has ≥ 1 candidate.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, UserPlus, Search, ClipboardList, Paperclip, Trash2, Users, Download,
} from 'lucide-react';

const SOURCES = ['linkedin', 'computrabajo', 'konzerta', 'referral', 'client_referral', 'hm_database', 'website', 'other'];

function AddCandidateDialog({ open, onOpenChange, processId, prefillCandidate, onAdded }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: '', email: '', phone: '', source: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setF({
        name: prefillCandidate?.name || '',
        email: prefillCandidate?.email || '',
        phone: prefillCandidate?.phone || '',
        source: '',
      });
      setFile(null);
    }
  }, [open, prefillCandidate]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.source) {
      toast.error(t('recruit2.pool.requiredMissing', { defaultValue: 'Completa nombre, correo y fuente.' }));
      return;
    }
    setSaving(true);
    try {
      const doc = await api.post(`/admin/recruitment-processes/${processId}/candidates`, {
        name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() || null, source: f.source,
      }).then((r) => r.data);
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/admin/recruitment-processes/${processId}/candidates/${doc.id}/cv`, fd);
      }
      toast.success(t('recruit2.pool.added', { defaultValue: 'Candidato agregado.' }));
      onOpenChange(false);
      onAdded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pool-add-dialog">
        <DialogHeader>
          <DialogTitle>{t('recruit2.pool.addNew', { defaultValue: 'Agregar candidato' })}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t('recruit2.pool.name', { defaultValue: 'Nombre' })} *</Label>
            <Input data-testid="pool-add-name" value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div><Label>{t('recruit2.pool.email', { defaultValue: 'Correo' })} *</Label>
            <Input data-testid="pool-add-email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
          <div><Label>{t('recruit2.pool.phone', { defaultValue: 'Teléfono' })}</Label>
            <Input data-testid="pool-add-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
          <div><Label>{t('recruit2.pool.source', { defaultValue: 'Fuente del candidato' })} *</Label>
            <Select value={f.source} onValueChange={(v) => set('source', v)}>
              <SelectTrigger data-testid="pool-add-source"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>{t(`recruit2.pool.sources.${s}`, { defaultValue: s })}</SelectItem>))}
              </SelectContent>
            </Select></div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" data-testid="pool-add-cv-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="pool-add-cv-btn">
            <Paperclip className="h-3.5 w-3.5" /> {t('recruit2.pool.uploadCv', { defaultValue: 'Subir CV (PDF/DOCX)' })}
          </Button>
          {file && <span className="text-xs text-muted-foreground">{file.name}</span>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
          <Button onClick={submit} disabled={saving} data-testid="pool-add-submit">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('recruit2.pool.add', { defaultValue: 'Agregar' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExistingCandidateDialog({ open, onOpenChange, processId, companyId, poolEmails, onAdded }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ(''); setLoading(true);
    api.get('/admin/users').then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setUsers(items.filter((u) => u.role === 'evaluated' && u.company_id === companyId && !u.recruitment_candidate));
    }).catch(() => setUsers([])).finally(() => setLoading(false));
  }, [open, companyId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = users.filter((u) => !poolEmails.has((u.email || '').toLowerCase()));
    if (!s) return base;
    return base.filter((u) => [u.name, u.email, u.position].some((v) => (v || '').toLowerCase().includes(s)));
  }, [users, q, poolEmails]);

  const add = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/recruitment-processes/${processId}/candidates/from-user`, { user_id: u.id });
      toast.success(t('recruit2.pool.added', { defaultValue: 'Candidato agregado.' }));
      onAdded();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto" data-testid="pool-existing-dialog">
        <DialogHeader>
          <DialogTitle>{t('recruit2.pool.searchExisting', { defaultValue: 'Buscar candidato existente' })}</DialogTitle>
          <DialogDescription>{t('recruit2.pool.searchExistingHint', { defaultValue: 'Colaboradores evaluados de la empresa del proceso.' })}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-3 text-muted-foreground" />
          <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pool-existing-search"
            placeholder={t('common.search', { defaultValue: 'Buscar…' })} />
        </div>
        {loading ? (
          <div className="py-8 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="pool-existing-empty">
            {t('recruit2.pool.existingEmpty', { defaultValue: 'No hay colaboradores evaluados disponibles.' })}
          </p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {filtered.map((u) => (
              <div key={u.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2" data-testid={`pool-existing-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}{u.position ? ` · ${u.position}` : ''}</p>
                </div>
                <Button size="sm" variant="outline" disabled={busyId === u.id} onClick={() => add(u)} data-testid={`pool-existing-add-${u.id}`}>
                  {busyId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('recruit2.pool.add', { defaultValue: 'Agregar' })}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignAssessmentsDialog({ open, onOpenChange, processId, pool, onDone }) {
  const { t } = useTranslation();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedQ, setPickedQ] = useState(new Set());
  const [pickedC, setPickedC] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPickedQ(new Set()); setPickedC(new Set(pool.map((c) => c.id))); setResult(null); setLoading(true);
    api.get('/admin/questionnaires').then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setQuestionnaires(items.filter((q) => q.status === 'published'
        && (q.suite_codes || []).includes('talentcheck') && q.current_version_id));
    }).catch(() => setQuestionnaires([])).finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (set, setter) => (id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/recruitment-processes/${processId}/assign-assessments`, {
        candidate_ids: [...pickedC], questionnaire_ids: [...pickedQ],
      }).then((r) => r.data);
      setResult(res);
      toast.success(t('recruit2.pool.assignDone', { defaultValue: 'Evaluaciones asignadas.' }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && result) onDone(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pool-assign-dialog">
        <DialogHeader>
          <DialogTitle>{t('recruit2.pool.assign', { defaultValue: 'Asignar evaluaciones' })}</DialogTitle>
          <DialogDescription>{t('recruit2.pool.assignHint', { defaultValue: 'Selecciona las evaluaciones de la Biblioteca y los candidatos que las recibirán.' })}</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="pool-assign-result">
            <p className="text-sm">
              {(result.created || []).length} {t('recruit2.pool.assignedCount', { defaultValue: 'asignaciones creadas' })}
              {(result.skipped || []).length > 0 && ` · ${(result.skipped || []).length} ${t('recruit2.pool.skipped', { defaultValue: 'omitidas (ya existían)' })}`}
            </p>
            {(result.created_accounts || []).length > 0 && (
              <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
                <p className="text-xs font-semibold mb-2">
                  {t('recruit2.pool.createdAccounts', { defaultValue: 'Cuentas creadas — comparte estas credenciales con los candidatos:' })}
                </p>
                {result.created_accounts.map((a) => (
                  <p key={a.email} className="text-xs font-mono" data-testid={`pool-cred-${a.email}`}>
                    {a.email} · {a.temp_password}
                  </p>
                ))}
              </div>
            )}
            {(result.errors || []).length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                {result.errors.map((er, i) => (<p key={i} className="text-xs text-destructive">{er.message}</p>))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => { onDone(); onOpenChange(false); }} data-testid="pool-assign-done">
                {t('common.close', { defaultValue: 'Cerrar' })}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {t('recruit2.pool.selectAssessments', { defaultValue: 'Evaluaciones (TalentCheck)' })}
              </p>
              {loading ? (
                <div className="py-4 flex justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>
              ) : (
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {questionnaires.map((q) => (
                    <label key={q.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer">
                      <Checkbox checked={pickedQ.has(q.id)} onCheckedChange={() => toggle(pickedQ, setPickedQ)(q.id)}
                        data-testid={`pool-assign-q-${q.id}`} />
                      <span className="text-sm">{q.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {t('recruit2.pool.selectCandidates', { defaultValue: 'Candidatos' })}
              </p>
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {pool.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 cursor-pointer">
                    <Checkbox checked={pickedC.has(c.id)} onCheckedChange={() => toggle(pickedC, setPickedC)(c.id)}
                      data-testid={`pool-assign-c-${c.id}`} />
                    <span className="text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
              <Button onClick={submit} disabled={busy || pickedQ.size === 0 || pickedC.size === 0} data-testid="pool-assign-submit">
                {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t('recruit2.pool.assignConfirm', { defaultValue: 'Asignar' })}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CandidatePoolPanel({ processId, companyId, stage, prefillCandidate, onChanged }) {
  const { t } = useTranslation();
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [existOpen, setExistOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const readOnly = stage === 11;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/recruitment-processes/${processId}/candidates-pool`).then((r) => r.data);
      setPool(res.items || []);
    } catch { setPool([]); }
    finally { setLoading(false); }
  }, [processId]);
  useEffect(() => { load(); }, [load]);

  const poolEmails = useMemo(() => new Set(pool.map((c) => (c.email || '').toLowerCase())), [pool]);

  const remove = async (c) => {
    try {
      await api.delete(`/admin/recruitment-processes/${processId}/candidates/${c.id}`);
      toast.success(t('recruit2.pool.removed', { defaultValue: 'Candidato eliminado.' }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCv = async (c) => {
    try {
      const res = await api.get(`/admin/recruitment-processes/${processId}/candidates/${c.id}/cv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = c.cv_file?.filename || 'cv';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-5 shadow-card" data-testid="candidate-pool-panel">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Users className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold">{t('recruit2.pool.title', { defaultValue: 'Candidatos' })}</h3>
        <Badge variant="outline" className="text-xs tabular-nums">{pool.length}</Badge>
        {!readOnly && (
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setExistOpen(true)} data-testid="pool-existing-btn">
              <Search className="h-3.5 w-3.5" /> {t('recruit2.pool.searchExisting', { defaultValue: 'Buscar candidato existente' })}
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="pool-add-btn">
              <UserPlus className="h-3.5 w-3.5" /> {t('recruit2.pool.addNew', { defaultValue: 'Agregar candidato' })}
            </Button>
            <Button size="sm" variant="secondary" className="gap-1.5" disabled={pool.length === 0}
              onClick={() => setAssignOpen(true)} data-testid="pool-assign-btn">
              <ClipboardList className="h-3.5 w-3.5" /> {t('recruit2.pool.assign', { defaultValue: 'Asignar evaluaciones' })}
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {pool.length === 0
          ? t('recruit2.pool.needOne', { defaultValue: 'Agrega al menos un candidato para habilitar la asignación de evaluaciones.' })
          : t('recruit2.pool.hint', { defaultValue: 'Lista de candidatos de la vacante. Asigna evaluaciones cuando la selección esté lista.' })}
      </p>
      {loading ? (
        <div className="py-6 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : pool.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="pool-empty">
          {t('recruit2.pool.empty', { defaultValue: 'Aún no hay candidatos en este proceso.' })}
        </p>
      ) : (
        <div className="space-y-2">
          {pool.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2" data-testid={`pool-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
              </div>
              <Badge variant="secondary" className="text-xs" data-testid={`pool-source-${c.id}`}>
                {t(`recruit2.pool.sources.${c.source}`, { defaultValue: c.source })}
              </Badge>
              <Badge variant="outline" className={`text-xs ${c.status === 'assessing' ? 'bg-success/10 text-success border-success/20' : ''}`}>
                {c.status === 'assessing'
                  ? t('recruit2.pool.statusAssessing', { defaultValue: 'En evaluación' })
                  : t('recruit2.pool.statusPool', { defaultValue: 'En selección' })}
              </Badge>
              {c.cv_file && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadCv(c)} data-testid={`pool-cv-${c.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              )}
              {!readOnly && c.status === 'pool' && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(c)} data-testid={`pool-remove-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <AddCandidateDialog open={addOpen} onOpenChange={setAddOpen} processId={processId}
        prefillCandidate={pool.length === 0 ? prefillCandidate : null}
        onAdded={() => { load(); onChanged && onChanged(); }} />
      <ExistingCandidateDialog open={existOpen} onOpenChange={setExistOpen} processId={processId}
        companyId={companyId} poolEmails={poolEmails} onAdded={() => { load(); onChanged && onChanged(); }} />
      <AssignAssessmentsDialog open={assignOpen} onOpenChange={setAssignOpen} processId={processId}
        pool={pool} onDone={() => { load(); onChanged && onChanged(); }} />
    </Card>
  );
}

export default CandidatePoolPanel;
